import React from 'react';

import * as Yup from 'yup';
import { Box, ButtonV2, DialogV2, Flex, FormControl, InputV2, TextArea } from '@withjoy/joykit';

import { useFormik } from 'formik';
import { ReactComponent as IconBrandLogoDesktop } from '@assets/icons/icon-brand-logo-desktop.svg';
import { useTranslation } from '@shared/core';
import { OpenInNewWindow } from '@withjoy/joykit/icons';

import { ProductType, SearchResultPdpModalForm } from '../../UniversalProdutcSearch.types';
import { formatPriceInputValue } from './SearchResultPdpModal.utils';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

type SearchResultPdpModalProps = {
  isOpen: boolean;
  onClose: () => void;
  searchResult: ProductType | null;
  onSubmitForm: (args: SearchResultPdpModalForm) => void;
};

const SearchResultPdpModalBody = (props: { searchResult: ProductType } & Pick<SearchResultPdpModalProps, 'onClose' | 'searchResult' | 'onSubmitForm'>) => {
  const { searchResult, onClose, onSubmitForm } = props;
  const { t } = useTranslation('catalogRegistry');

  const pdpModalTrans = t('universalSearch', 'pdpModal');

  const formik = useFormik<SearchResultPdpModalForm>({
    initialValues: {
      title: searchResult?.title || '',
      price: `${searchResult.salePrice?.floatingPointDecimalString ?? searchResult.listPrice.floatingPointDecimalString}`,
      quantity: 1,
      note: ''
    },
    validationSchema: Yup.object<SearchResultPdpModalForm>({
      title: Yup.string().required(),
      price: Yup.string().required(),
      quantity: Yup.number().required(),
      note: Yup.string()
    }),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: values => {
      onSubmitForm(values);
    }
  });

  const handleOnQuantityInputBlur = useEventCallback<React.FocusEventHandler<HTMLInputElement>>(e => {
    const value = e.currentTarget.value;
    const parsedValue = parseFloat(value);
    if (Number.isNaN(parsedValue) || parsedValue < 1) {
      formik.setFieldValue('quantity', 1);
    }
  });

  return (
    <>
      <DialogV2.Body>
        <Flex flexDirection={['column', null, 'row']} columnGap={[null, null, 7]} rowGap={[7, null, 0]}>
          {/* Product photo */}
          <Box width={['100%', null, null, '40%']} maxWidth={[null, null, null, '40%']}>
            <Flex flexDirection="column" rowGap={5}>
              <Box
                __css={{ width: '100%', paddingTop: '100%', backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}
                backgroundImage={`url('${searchResult?.photos[0].url}')`}
              />
              <ButtonV2 as="a" target="_blank" href={searchResult?.url} variant="link" intent="neutral" typographyVariant={'button1'} endIcon={<OpenInNewWindow />}>
                <Box as="span" textOverflow="ellipsis" overflow="hidden">
                  {pdpModalTrans.viewShopOnLink({ shopName: searchResult?.title })}
                </Box>
              </ButtonV2>
            </Flex>
          </Box>

          {/* Details */}
          <Flex flexDirection="column" rowGap={7}>
            <FormControl label={pdpModalTrans.formFields.title.label()} isInvalid={!!formik.errors.title}>
              <InputV2 {...formik.getFieldProps('title')} />
            </FormControl>
            <Flex columnGap={6}>
              <FormControl label={'Price'} isInvalid={!!formik.errors.price}>
                <InputV2
                  type="text"
                  pattern="[0-9]*(.[0-9]+)?"
                  inputMode="decimal"
                  {...formik.getFieldProps('price')}
                  value={formatPriceInputValue(formik.values.price)}
                  isReadOnly={true}
                />
              </FormControl>
              <FormControl label={pdpModalTrans.formFields.quantity.label()} isInvalid={!!formik.errors.quantity}>
                <InputV2 type="number" min={1} {...formik.getFieldProps('quantity')} onBlur={handleOnQuantityInputBlur} />
              </FormControl>
            </Flex>

            <FormControl label={pdpModalTrans.formFields.note.label()}>
              <TextArea placeholder={pdpModalTrans.formFields.note.placeholder()} {...formik.getFieldProps('note')} resize="vertical" />
            </FormControl>
          </Flex>
        </Flex>
      </DialogV2.Body>
      <DialogV2.Footer justifyContent={'space-between'}>
        <ButtonV2 variant="ghost" intent="neutral" onClick={onClose}>
          {pdpModalTrans.cancelCta()}
        </ButtonV2>
        <ButtonV2 intent="neutral" disabled={!formik.isValid} onClick={() => formik.submitForm()}>
          {pdpModalTrans.addCta()}
        </ButtonV2>
      </DialogV2.Footer>
    </>
  );
};

export const SearchResultPdpModal = (props: SearchResultPdpModalProps) => {
  const { isOpen, onClose, searchResult, onSubmitForm } = props;

  return (
    <DialogV2
      isOpen={isOpen}
      onClose={onClose}
      enableDividers
      size="2xl"
      stickyBehavior="header-footer"
      overrides={{
        CloseButton: {
          props: {
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'mono5',
            height: 8,
            width: 8,
            top: 6,
            right: 7,
            color: 'mono14',
            minWidth: 'unset',
            padding: 0,
            borderRadius: 'full'
          }
        }
      }}
    >
      {searchResult && (
        <>
          <DialogV2.Header>
            <IconBrandLogoDesktop />
            <DialogV2.CloseButton />
          </DialogV2.Header>
          <SearchResultPdpModalBody searchResult={searchResult} onClose={onClose} onSubmitForm={onSubmitForm} />
        </>
      )}
    </DialogV2>
  );
};
